import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Backdrop from './Backdrop.web';
import Popover from './Popover.web';
import {PopableProps} from '../tooltip_types.web';
import {popableStyles} from '../tooltip_styles.web';

export type PopableManager = {
  hide: () => void;
  show: () => void;
};

const DEFAULT_LAYOUT = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

const Popable = forwardRef<PopableManager, PopableProps>(function Popable(
  {
    id,
    className,
    action = 'press',
    animated,
    backgroundColor,
    children,
    caret,
    textAlign,
    caretPosition,
    content,
    numberOfLines,
    onAction,
    position = 'top',
    strictPosition = false,
    style,
    visible,
    wrapperStyle,
  },
  ref,
) {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverLayout, setPopoverLayout] = useState(DEFAULT_LAYOUT);
  const [childrenLayout, setChildrenLayout] = useState(DEFAULT_LAYOUT);
  const [computedPosition, setComputedPosition] = useState(position);
  const isInteractive = typeof visible === 'undefined';
  const isPopoverVisible = isInteractive ? popoverVisible : visible;
  const childrenRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    show: () => setPopoverVisible(true),
    hide: () => setPopoverVisible(false),
  }));

  const handleHidePopover = () => {
    setPopoverVisible(false);
    onAction?.(false);
  };

  const updatePopoverLayout = useCallback(() => {
    if (popoverRef.current) {
      const rect = popoverRef.current.getBoundingClientRect();
      setPopoverLayout({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, []);

  const updateChildrenLayout = useCallback(() => {
    if (childrenRef.current) {
      const rect = childrenRef.current.getBoundingClientRect();
      setChildrenLayout({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, []);

  const handleMouseEnter = () => {
    if (action === 'hover') {
      setPopoverVisible(true);
      onAction?.(true);
    }
  };

  useEffect(() => {
    updatePopoverLayout();
    updateChildrenLayout();
  }, [popoverVisible, updatePopoverLayout, updateChildrenLayout]);

  useEffect(() => {
    let nextPosition = position;

    if (!strictPosition) {
      const {innerWidth: screenWidth, innerHeight: screenHeight} = window;
      switch (position) {
        case 'left':
          if (popoverLayout.x <= 0) nextPosition = 'right';
          break;
        case 'right':
          if (popoverLayout.x + popoverLayout.width > screenWidth)
            nextPosition = 'left';
          break;
        case 'top':
          if (popoverLayout.y <= 0) nextPosition = 'bottom';
          break;
        case 'bottom':
          if (popoverLayout.y + popoverLayout.height >= screenHeight)
            nextPosition = 'top';
          break;
      }
    }

    setComputedPosition(nextPosition);
  }, [position, strictPosition, popoverLayout, childrenLayout]);

  useEffect(() => {
    let left = 0;
    let top = 0;

    switch (computedPosition) {
      case 'right':
      case 'left':
        top = (popoverLayout.height - childrenLayout.height) / 2;
        break;
      case 'top':
      case 'bottom':
        left = (popoverLayout.width - childrenLayout.width) / 2;
        break;
    }
  }, [computedPosition, popoverLayout, childrenLayout]);

  const sharedPopoverProps = {
    animated,
    backgroundColor,
    caret,
    textAlign,
    caretPosition,
    children: content,
    numberOfLines,
    position,
  };

  const toolTipElement = document?.getElementById('tooltipElement');
  toolTipElement?.addEventListener('mouseleave', (e: Event) => {
    if (action === 'hover') {
      setPopoverVisible(false);
      onAction?.(false);
    }
  });

  return (
    <div
      id={id}
      className={className}
      style={{...popableStyles.container, ...wrapperStyle}}>
      <Backdrop
        visible={isInteractive && popoverVisible}
        onClick={
          action === 'press' || action === 'longpress'
            ? handleHidePopover
            : () => {}
        }
        style={{
          cursor:
            action === 'press' || action === 'longpress' ? 'pointer' : 'auto',
        }}
        popoverRef={popoverRef}
        childrenRef={childrenRef}>
        <Popover
          {...sharedPopoverProps}
          visible={isPopoverVisible}
          ref={popoverRef}
          style={{
            ...style,
            position: 'absolute',
          }}
        />
      </Backdrop>

      <div
        ref={childrenRef}
        onMouseEnter={handleMouseEnter}
        onClick={() => {
          onAction?.(!visible);
          setPopoverVisible(!visible);
        }}
        style={{
          cursor: action !== 'hover' ? 'pointer' : 'default',
          height: 'fit-content',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: visible ? 'none' : 'auto',
        }}>
        {children}
      </div>
    </div>
  );
});

export default Popable;
